import { useCallback, useEffect, useMemo, useState } from "react";
import {
  clearDemoKuliah,
  createQualifiedDemo,
  markDemoRendered,
  publishDemoKuliah,
  resetDemoSession,
  sendDemoAnnouncement,
  setDemoEtvPreview,
  subscribeDemoSession,
} from "./demoService";
import { isDemoSessionExpired } from "./demoContract";

const STORAGE_KEY = "etikafDemoSessionId";

const defaultService = {
  clearDemoKuliah,
  createQualifiedDemo,
  markDemoRendered,
  publishDemoKuliah,
  resetDemoSession,
  sendDemoAnnouncement,
  setDemoEtvPreview,
  subscribeDemoSession,
};

const browserStorage = () =>
  typeof window === "undefined"
    ? { getItem: () => null, setItem: () => {}, removeItem: () => {} }
    : window.sessionStorage;

const expiryMillis = (expiresAt) => {
  if (typeof expiresAt?.toMillis === "function") return expiresAt.toMillis();
  if (expiresAt instanceof Date) return expiresAt.getTime();
  return Date.parse(expiresAt) || 0;
};

export const useDemoSession = (options = {}) => {
  const service = options.service || defaultService;
  const storage = useMemo(() => options.storage || browserStorage(), [options.storage]);
  const tickMs = options.tickMs || 1_000;
  const [sessionId, setSessionId] = useState(() => storage.getItem(STORAGE_KEY) || "");
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState(sessionId ? "loading" : "lead");
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!sessionId) return undefined;
    setPhase("loading");
    return service.subscribeDemoSession(sessionId, {
      next: (nextSession) => {
        if (!nextSession) {
          setError("Sesi demo tidak ditemui.");
          setPhase("error");
          return;
        }
        setSession(nextSession);
        setPhase(isDemoSessionExpired(nextSession) ? "expired" : "demo");
      },
      error: () => {
        setError("Sesi demo belum dapat dibaca. Sila cuba lagi.");
        setPhase("error");
      },
    });
  }, [service, sessionId]);

  useEffect(() => {
    if (!session?.expiresAt) return undefined;
    const updateCountdown = () => {
      const seconds = Math.max(0, Math.ceil((expiryMillis(session.expiresAt) - Date.now()) / 1_000));
      setRemainingSeconds(seconds);
      if (seconds === 0) setPhase("expired");
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, tickMs);
    return () => clearInterval(interval);
  }, [session, tickMs]);

  const create = useCallback(
    async (lead) => {
      if (creating) return null;
      setCreating(true);
      setError("");
      setPhase("creating");
      try {
        const result = await service.createQualifiedDemo(lead);
        storage.setItem(STORAGE_KEY, result.sessionId);
        setSessionId(result.sessionId);
        return result;
      } catch (createError) {
        setError(createError.userMessage || "Demo belum dapat dimulakan.");
        setPhase("lead");
        throw createError;
      } finally {
        setCreating(false);
      }
    },
    [creating, service, storage]
  );

  const send = useCallback(
    async (announcement) => {
      if (!sessionId || !session || isDemoSessionExpired(session) || sending) return null;
      setSending(true);
      setError("");
      try {
        return await service.sendDemoAnnouncement({ sessionId, announcement });
      } catch (sendError) {
        setError(sendError.userMessage || "Pengumuman belum dapat dihantar.");
        throw sendError;
      } finally {
        setSending(false);
      }
    },
    [sending, service, session, sessionId]
  );

  const reset = useCallback(async () => {
    if (!sessionId || !session || isDemoSessionExpired(session)) return null;
    setError("");
    return service.resetDemoSession(sessionId);
  }, [service, session, sessionId]);

  const publishKuliah = useCallback(
    async (kuliah) => {
      if (!sessionId || !session || isDemoSessionExpired(session)) return null;
      setError("");
      try {
        return await service.publishDemoKuliah({ sessionId, kuliah });
      } catch (publishError) {
        setError(publishError.userMessage || "Kuliah belum dapat disimpan.");
        throw publishError;
      }
    },
    [service, session, sessionId]
  );

  const clearKuliah = useCallback(() => {
    if (!sessionId || !session || isDemoSessionExpired(session)) return Promise.resolve();
    setError("");
    return service.clearDemoKuliah(sessionId);
  }, [service, session, sessionId]);

  const setEtvPreview = useCallback(
    (mode, prayer = "Maghrib") => {
      if (!sessionId || !session || isDemoSessionExpired(session)) return Promise.resolve();
      setError("");
      return service.setDemoEtvPreview({ sessionId, mode, prayer });
    },
    [service, session, sessionId]
  );

  const startOver = useCallback(() => {
    storage.removeItem(STORAGE_KEY);
    setSessionId("");
    setSession(null);
    setRemainingSeconds(0);
    setError("");
    setPhase("lead");
  }, [storage]);

  const markRendered = useCallback(
    (target, revision, contentType) => {
      if (!sessionId || !session || isDemoSessionExpired(session)) return Promise.resolve();
      return service.markDemoRendered({ sessionId, target, revision, contentType });
    },
    [service, session, sessionId]
  );

  return {
    phase,
    leadForm: { creating },
    session,
    sessionId,
    remainingSeconds,
    clearKuliah,
    create,
    publishKuliah,
    send,
    reset,
    setEtvPreview,
    startOver,
    markRendered,
    sending,
    error,
  };
};

export default useDemoSession;
